import { all, create } from 'mathjs';
import { cloneDeep, findKey, isFinite, isNumber, uniqBy } from 'lodash';
import {
  CLIENT_TYPE,
  CreditRating,
  FinanceType,
  IncentivesProvider,
  LeaseMileageNames,
  LeaseTermNames,
  Origin,
} from '@upstart/harmony/lib/constants';
import { Condition } from '@upstart/harmony/lib/legos/constants';
import {
  AprConfig,
  Dealer,
  PurchaseTerms,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { PriceRules } from '@upstart/harmony/lib/legos/priceRulesTypes';
import {
  Car,
  IncentivesProgram,
  IncentivesProgramOffer,
} from '@upstart/harmony/lib/legos/carTypes';
import {
  LeaseOffer,
  Offer,
  PurchaseOffer,
  RawLeaseOffer,
  RawPurchaseOffer,
} from '@upstart/harmony/lib/legos/paymentTypes';
import { getAprConfigForCar } from '@upstart/harmony/lib/car-utils';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Lead, Rebate } from './rebate-types';
import { RATE_TO_APR } from '../../../util/constants';

export type OfferIncentivesPair = {
  offer: PurchaseOffer | LeaseOffer;
  incentives: IncentivesProgramOffer[];
};

const math = create(all, {});
const BASIS_MONEY_FACTOR_TO_APR = 0.00024;

// Gets the correct APR markup (lease only)
const getAprWithMarkup = (
  car: Car,
  dealer: Dealer,
  dealerPriceRules: PriceRules | null,
  term: number,
  mileage: number,
  rawApr: number,
  creditRating: CreditRating,
) => {
  const aprConfig = getAprConfigForCar(
    car,
    dealer.inStoreAprConfig || null,
    dealerPriceRules,
    CLIENT_TYPE.IN_STORE,
    car.condition ?? Condition.NEW,
  );
  if (aprConfig?.enabled) {
    const mileageKey = findKey(
      aprConfig?.lease?.mileages,
      m => m === mileage,
    ) as LeaseMileageNames;
    const termKey = Object.entries(aprConfig?.lease?.terms ?? {}).find(
      ([, termObj]) => termObj?.term === term,
    )?.[0] as LeaseTermNames;

    const basisPointsMoneyFactorMarkup =
      aprConfig?.lease?.terms?.[termKey]?.credit?.[creditRating]?.moneyFactor?.[
        mileageKey
      ] || 0;
    if (
      !!math.bignumber &&
      isFinite(basisPointsMoneyFactorMarkup) &&
      basisPointsMoneyFactorMarkup > 0
    ) {
      const aprMarkup = math
        .bignumber(BASIS_MONEY_FACTOR_TO_APR)
        .mul(basisPointsMoneyFactorMarkup)
        .toNumber();
      return rawApr + aprMarkup;
    }
  }
  return rawApr;
};

// Checks to see if rebates are enabled before returning rebates
const getRebatesWithConfig = (
  dealer: Dealer,
  rawCash: number | undefined,
  rawDealerCash: number | undefined,
) => {
  const aprConfig = dealer.inStoreAprConfig?.New;
  if (aprConfig?.enabled && aprConfig?.options?.useManufacturerRebate?.lease) {
    return { cash: rawCash, dealerCash: rawDealerCash };
  }
  // defaults to false
  return { cash: 0, dealerCash: 0 };
};

// Look into the OEM offers and find matching programs.
// Typically this is needed if we are switching a lease term.
export const getNewLeaseProgramsFromOemOffers = (
  dealer: Dealer,
  dealerPriceRules: PriceRules | null,
  car: Car,
  term: number,
  mileage: number,
  creditRating: CreditRating,
) => {
  const offers = car?.oemOffers?.[FinanceType.LEASE] as RawLeaseOffer[];
  const matchingOffer = offers?.find(
    offer => offer.from <= term && term <= offer.to,
  );
  if (matchingOffer && matchingOffer.apr) {
    const {
      apr: rawApr,
      programs,
      cash: rawCash,
      dealerCash: rawDealerCash,
    } = matchingOffer;

    // Fetch APR and Rebates based on config
    const apr = getAprWithMarkup(
      car,
      dealer,
      dealerPriceRules,
      term,
      mileage,
      rawApr,
      creditRating,
    );
    const { cash, dealerCash } = getRebatesWithConfig(
      dealer,
      rawCash,
      rawDealerCash,
    );

    const selectResidual = (program: IncentivesProgram) =>
      program.residuals?.[term]?.[mileage];
    const residualProgram = programs?.find(selectResidual);
    if (residualProgram) {
      const residualPercentage = selectResidual(residualProgram);
      return { apr, programs, cash, dealerCash, residualPercentage };
    }
  }
  return {};
};

// When displaying lease incentives, if we see we have a markup for the rate configured for the dealer, add it here
export const addMarkupToLeaseRates = (
  incentiveOffers: RawLeaseOffer[],
  activeOffer: LeaseOffer,
  deal: Deal,
  dealer: Dealer,
  isOnePay: boolean,
) =>
  incentiveOffers.map(o => {
    // Not an active offer selected yet, do nothing (happens when modal is initialized first and rest of components are not loaded)
    if (!activeOffer) {
      return o;
    }
    const mileageNumberBeingEdited = activeOffer.mileage;
    const termBeingEdited = activeOffer.months;
    if (!isNumber(mileageNumberBeingEdited)) {
      // It's not an offer from a lease row, so we don't touch the offer at all
      return o;
    }
    if (!isNumber(o.apr)) {
      // It's not a Rate/Apr offer, so nothing to do here
      return o;
    }

    // Pick between the online or instore configurations
    const dealerAprConfig =
      deal.origin === Origin.ONLINE
        ? dealer.aprConfig
        : dealer.inStoreAprConfig;
    if (!dealerAprConfig?.New?.enabled) {
      // Setting in Chairman for custom aprs is not enabled, so we don't modify anything
      return o;
    }
    // So far seems all conditions are good, so we continue to add the Lease Markups to the incentive

    const leaseMileages = dealerAprConfig.New.lease?.mileages || {};
    // Based on the mileage number (10000), we get the corresponding termKey (mileage1).
    const [termKey] =
      Object.entries(leaseMileages).find(
        ([_, value]) => value === mileageNumberBeingEdited,
      ) || [];
    if (!termKey) {
      return o;
    }

    // Now, within the configuration for the apr, we check if we have this term configured
    const termConfig = Object.values(
      dealerAprConfig.New.lease?.terms || {},
    ).find(t => t?.term === termBeingEdited);
    if (!termConfig) {
      return o;
    }

    // Since the configuration is also different per credit rating, this matters
    const creditRating = deal.creditRating || CreditRating.EXCELLENT;

    // Depending if it's One Pay, we have different configuration as well
    const moneyFactorKey = isOnePay ? 'onePayLeaseMoneyFactor' : 'moneyFactor';

    // Finally get the markup number, as basis points
    const markup =
      termConfig?.credit?.[creditRating as CreditRating]?.[moneyFactorKey]?.[
        termKey as LeaseMileageNames
      ];

    // We didn't find the markup, or markup is zero, so we bail out
    if (!markup) {
      return o;
    }

    // Convert the markup from basis money factor to apr
    const markupAsRate = markup * BASIS_MONEY_FACTOR_TO_APR;
    // Clone offer so we don't do cumulative sums on re-renders
    const newOffer = cloneDeep(o);
    newOffer.apr = (newOffer.apr || 0) + markupAsRate;
    return newOffer;
  });

/**
 * Returns a copy of the passed purchase offers, with APR adjusted if an OEM
 * markup should be applied.
 *
 * @param incentiveOffers the purchase offers to copy, and adjust if necessary
 * @param activeOffer the active purchase offer
 * @param deal the deal for the active offer
 * @param dealer the dealer for the deal
 * @returns a copy of the passed purchase offers, with APR adjusted if an OEM
 * markup should be applied.
 */
export const addMarkupToPurchaseApr = (
  incentiveOffers: RawPurchaseOffer[],
  activeOffer: PurchaseOffer,
  deal: Deal,
  dealer: Dealer,
): RawPurchaseOffer[] =>
  incentiveOffers.map((e: RawPurchaseOffer) => {
    /* Clone the incentive offer so that no matter what, we're returning a copy
     * that can never mutate the original. */
    const incentiveOffer: RawPurchaseOffer = cloneDeep(e);

    const activeOfferMonths: number | undefined = activeOffer.months;
    const incentiveApr: number | undefined = incentiveOffer.apr;

    /* If no active offer is yet selected, or the active offer does not have
     * months (and is thus probably not a purchase offer), or the incentive
     * offer does not have an APR, no markup can be applied, so return the
     * incentive offer without modification. */
    if (
      !activeOffer ||
      !isNumber(activeOfferMonths) ||
      !isNumber(incentiveApr) ||
      incentiveApr <= 0
    ) {
      return incentiveOffer;
    }

    /* Lookup the OEM markup for new vehicles from the dealer config, from the
     * config appropriate for the UAR origin used for the deal. */
    const dealerAprConfigNew: AprConfig =
      deal.origin === Origin.ONLINE
        ? dealer.aprConfig?.New ?? {}
        : dealer.inStoreAprConfig?.New ?? {};

    /* If no OEM markup for purchase is configured, return the incentive offer
     * without modification. */
    if (!dealerAprConfigNew.enabled) {
      return incentiveOffer;
    }

    const purchaseTermsConfig: PurchaseTerms =
      dealerAprConfigNew.purchase?.terms || ({} as PurchaseTerms);

    const [termKey]: [keyof PurchaseTerms, PurchaseTerms['term1']] =
      (Object.entries(purchaseTermsConfig).find(
        ([, termConfigMonths]) => termConfigMonths === activeOfferMonths,
      ) as [keyof PurchaseTerms, PurchaseTerms['term1']]) || [];

    /* If no OEM markup is configured for the number of months specified for
     * for the active purchase offer, return the incentive offer without
     * modification. */
    if (!termKey) {
      return incentiveOffer;
    }

    /* Use the deal's credit rating, or a default of excellent, to lookup the
     * OEM markup for purchases by credit tier. */
    const creditRating: CreditRating =
      deal.creditRating || CreditRating.EXCELLENT;

    const aprMarkupForCreditRatingAndTermConfig: number =
      dealerAprConfigNew.purchase?.credit?.[creditRating]?.markup?.[termKey] ||
      0;

    // If the OEM markup is a positive number, modify the incentive offer APR.
    if (
      isNumber(aprMarkupForCreditRatingAndTermConfig) &&
      aprMarkupForCreditRatingAndTermConfig > 0
    ) {
      incentiveOffer.apr =
        aprMarkupForCreditRatingAndTermConfig / 100 + incentiveApr;
    }

    return incentiveOffer;
  });

export const isIncentiveApplicable = (
  o: IncentivesProgramOffer,
  activeOffer: Offer,
) => {
  if (o.from && o.to) {
    return (
      o.from <= (activeOffer as PurchaseOffer).months &&
      (activeOffer as PurchaseOffer).months <= o.to
    );
  }
  // Display offers without to or from by default
  return true;
};

export const filterIncentives = (
  incentiveOffers: IncentivesProgramOffer[],
  offer: Offer,
) => {
  const activeOffer = offer as PurchaseOffer;
  const monthFiltered = incentiveOffers.filter(o =>
    isIncentiveApplicable(o, activeOffer),
  );
  return uniqBy(
    monthFiltered,
    incentive =>
      `${incentive.program.description}-${incentive.program.appliedCash ?? 0}`,
  );
};

const baseIncentiveProgram: IncentivesProgram = {
  id: '',
  appliedApr: null,
  category: '',
  description: '',
  programText: '',
  endDate: '',
  groupAffiliation: '',
  market: '',
  previousOwnership: '',
  programNumber: '',
  provider: IncentivesProvider.MARKETSCAN,
  source: '',
  startDate: '',
  appliedCash: 0,
  appliedDealerCash: 0,
};

export const rebateToIncentiveProgram = (
  rebate: Rebate,
): IncentivesProgram => ({
  ...cloneDeep(baseIncentiveProgram),
  id: rebate.rebateId.toString(),
  description: rebate.name,
  programText: rebate.details,
  programNumber: rebate.rebateId.toString(),
  endDate: rebate.expiresAt,
});

export const leadToIncentiveProgram = (lead: Lead): IncentivesProgram => {
  const incentivesProgram: IncentivesProgram = {
    ...cloneDeep(baseIncentiveProgram),
    id: lead.leadId.toString(),
    programNumber: lead.programId,
    description: lead.name,
    endDate: lead.expiresAt,
  };

  if (lead.apr) {
    incentivesProgram.appliedApr = lead.apr / 100;
  } else if (lead.moneyFactor) {
    incentivesProgram.appliedApr = lead.moneyFactor * RATE_TO_APR;
  }

  return incentivesProgram;
};

export const test = {
  addMarkupToLeaseRates,
  addMarkupToPurchaseApr,
  getAprWithMarkup,
  getRebatesWithConfig,
};
